import { motion } from 'framer-motion'
import { 
  ArrowLeft, 
  LogOut,
  Scale,
  FileText,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  BarChart3,
  Search,
  Gavel,
  BookOpen,
  Shield,
  AlertCircle,
  Calendar,
  Brain,
  Zap,
  Target,
  Activity
} from 'lucide-react'
import InfoTooltip from '../common/InfoTooltip'

export default function JuridicoDashboard({ onBack, onLogout, businessData }) {
  const kpis = [
    {
      title: 'Análisis de Documentos',
      value: '342',
      change: '+87',
      trend: 'up',
      icon: FileText,
      color: 'purple',
      suffix: '/mes',
      tooltip: {
        title: 'Análisis Automático de Documentos Legales',
        description: 'Cantidad de documentos legales procesados y analizados automáticamente por el sistema de IA legal.',
        aiMethod: 'NLP avanzado para análisis jurídico, extracción de entidades legales, clasificación automática de documentos.',
        dataSource: 'Base documental del despacho, contratos, sentencias, jurisprudencia, documentos de casos activos.',
        updateFrequency: 'Procesamiento continuo con reporte mensual'
      }
    },
    {
      title: 'Tiempo de Investigación',
      value: '65%',
      change: '-35%',
      trend: 'up',
      icon: Clock,
      color: 'green',
      description: 'Reducción',
      tooltip: {
        title: 'Reducción en Tiempo de Investigación Legal',
        description: 'Porcentaje de reducción en tiempo invertido en investigación jurídica gracias a búsquedas inteligentes.',
        aiMethod: 'Búsqueda semántica en bases jurídicas, AI para identificación de precedentes relevantes, resumen automático.',
        dataSource: 'Tiempo histórico de investigación, bases de jurisprudencia, bibliotecas legales digitales.',
        updateFrequency: 'Medición semanal con análisis mensual'
      }
    },
    {
      title: 'Precisión de Análisis',
      value: '94.2%',
      change: '+4.8%',
      trend: 'up',
      icon: Target,
      color: 'blue',
      tooltip: {
        title: 'Precisión del Análisis Legal por IA',
        description: 'Porcentaje de exactitud en el análisis automático de documentos legales y identificación de elementos clave.',
        aiMethod: 'Modelos de ML entrenados en corpus jurídico, validación cruzada con expertos legales.',
        dataSource: 'Validaciones de expertos legales, comparativa con análisis manual, feedback de casos reales.',
        updateFrequency: 'Evaluación continua con reporte quincenal'
      }
    },
    {
      title: 'Casos Activos',
      value: '78',
      change: '+12',
      trend: 'up',
      icon: Gavel,
      color: 'orange',
      tooltip: {
        title: 'Gestión de Casos Activos',
        description: 'Número de casos legales activos gestionados con asistencia de IA para seguimiento y análisis.',
        aiMethod: 'Sistema de gestión inteligente, predicción de outcomes, alertas automáticas de plazos.',
        dataSource: 'Expedientes digitales, calendarios procesales, documentos de casos, historial de resoluciones.',
        updateFrequency: 'Actualización diaria en tiempo real'
      }
    }
  ]

  const documentAnalysis = [
    {
      type: 'Contratos',
      processed: 156,
      accuracy: 96.5,
      avgTime: '3.2 min',
      keyInsights: 'Cláusulas de riesgo identificadas automáticamente',
      color: 'blue',
      tooltip: {
        title: 'Análisis Automático de Contratos',
        description: 'Procesamiento inteligente de contratos para identificar cláusulas clave, riesgos legales y términos importantes.',
        aiMethod: 'NLP para extracción de cláusulas, análisis de riesgo contractual, comparación con estándares legales.',
        dataSource: 'Base de contratos históricos, plantillas legales, normativas contractuales vigentes.',
        updateFrequency: 'Análisis inmediato al cargar documento'
      }
    },
    {
      type: 'Jurisprudencia',
      processed: 89,
      accuracy: 93.8,
      avgTime: '5.7 min',
      keyInsights: 'Precedentes relevantes encontrados en 2 seg',
      color: 'purple',
      tooltip: {
        title: 'Búsqueda Inteligente de Jurisprudencia',
        description: 'Sistema de búsqueda semántica que encuentra precedentes jurídicos relevantes para casos específicos.',
        aiMethod: 'Búsqueda vectorial semántica, análisis de similitud legal, ranking de relevancia judicial.',
        dataSource: 'Bases de jurisprudencia nacional, sentencias de tribunales, precedentes judiciales.',
        updateFrequency: 'Base actualizada semanalmente'
      }
    },
    {
      type: 'Demandas',
      processed: 67,
      accuracy: 98.1,
      avgTime: '4.1 min',
      keyInsights: 'Análisis de fortalezas/debilidades del caso',
      color: 'green',
      tooltip: {
        title: 'Análisis Estratégico de Demandas',
        description: 'Evaluación automática de la viabilidad legal y estrategia procesal de demandas y escritos judiciales.',
        aiMethod: 'Análisis predictivo de outcomes, evaluación de precedentes aplicables, scoring de fortaleza legal.',
        dataSource: 'Histórico de casos similares, tasas de éxito por tipo de demanda, análisis de tribunales.',
        updateFrequency: 'Análisis inmediato con actualizaciones de jurisprudencia'
      }
    },
    {
      type: 'Regulaciones',
      processed: 30,
      accuracy: 91.2,
      avgTime: '8.3 min',
      keyInsights: 'Cambios regulatorios detectados automáticamente',
      color: 'orange',
      tooltip: {
        title: 'Monitoreo de Regulaciones',
        description: 'Seguimiento automático de cambios normativos y su impacto en casos activos y práctica legal.',
        aiMethod: 'Análisis de cambios regulatorios, detección de impactos en casos, alertas de compliance.',
        dataSource: 'Diarios oficiales, boletines normativos, actualizaciones legislativas, registros regulatorios.',
        updateFrequency: 'Monitoreo diario con alertas inmediatas'
      }
    }
  ]

  const caseProgress = [
    {
      name: 'Caso Mercantil ABC vs XYZ',
      stage: 'Investigación',
      progress: 75,
      aiInsights: 'Alto potencial de éxito - 87%',
      priority: 'high',
      deadline: '15 Jun 2024'
    },
    {
      name: 'Divorcio - Rodriguez',
      stage: 'Mediación',
      progress: 60,
      aiInsights: 'Recomendación: acuerdo extrajudicial',
      priority: 'medium',
      deadline: '22 Jun 2024'
    },
    {
      name: 'Laboral - Constructora DEF',
      stage: 'Preparación',
      progress: 40,
      aiInsights: 'Documentos faltantes identificados',
      priority: 'low',
      deadline: '30 Jun 2024'
    }
  ]

  const researchStats = [
    { topic: 'Derecho Mercantil', searches: 234, successRate: 89 },
    { topic: 'Derecho Laboral', searches: 189, successRate: 92 },
    { topic: 'Derecho Civil', searches: 156, successRate: 85 },
    { topic: 'Derecho Penal', searches: 98, successRate: 94 },
    { topic: 'Derecho Fiscal', searches: 87, successRate: 88 }
  ]

  const alerts = [
    {
      type: 'warning',
      message: 'Vencimiento audiencia Caso ABC en 2 días',
      time: 'Hace 1 hora',
      action: 'Preparar documentos'
    },
    {
      type: 'info',
      message: 'Nueva jurisprudencia relevante para 3 casos activos',
      time: 'Hace 3 horas',
      action: 'Revisar impacto'
    },
    {
      type: 'success',
      message: 'Análisis completo de contrato - 47 cláusulas revisadas',
      time: 'Hace 5 horas',
      action: 'Ver reporte'
    }
  ]

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      purple: 'bg-purple-100 text-purple-800',
      orange: 'bg-orange-100 text-orange-800',
      red: 'bg-red-100 text-red-800'
    }
    return colors[color] || colors.blue
  }

  const getIconColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
      red: 'bg-red-500'
    }
    return colors[color] || colors.blue
  }

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    }
    return colors[priority] || colors.medium
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={onBack}
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors mr-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </button>
              <div className="flex items-center">
                <div className={`w-8 h-8 bg-gradient-to-br ${businessData.color} rounded-lg flex items-center justify-center mr-3`}>
                  <Scale className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">{businessData.title}</h1>
                  <p className="text-sm text-gray-500">Analytics Legal con IA</p>
                </div>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* KPIs Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {kpis.map((kpi, index) => {
            const IconComponent = kpi.icon
            return (
              <div key={index} className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm text-gray-600">{kpi.title}</p>
                      <InfoTooltip 
                        title={kpi.tooltip.title}
                        description={kpi.tooltip.description}
                        aiMethod={kpi.tooltip.aiMethod}
                        dataSource={kpi.tooltip.dataSource}
                        updateFrequency={kpi.tooltip.updateFrequency}
                        iconColor="text-gray-400"
                      />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {kpi.value}{kpi.suffix || ''}
                    </p>
                    <div className="flex items-center mt-2">
                      <span className={`text-sm font-medium ${kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                        {kpi.change}
                      </span>
                      <span className="text-sm text-gray-500 ml-1">
                        {kpi.description || 'vs mes anterior'}
                      </span>
                    </div>
                  </div>
                  <div className={`p-3 ${getIconColorClasses(kpi.color)} rounded-lg`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            )
          })}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Análisis de Documentos */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-semibold text-gray-900">Análisis de Documentos por IA</h3>
                  <InfoTooltip 
                    title="Motor de Análisis de Documentos Legales"
                    description="Sistema de IA especializado en el procesamiento automático y análisis de diferentes tipos de documentos legales."
                    aiMethod="NLP jurídico especializado, reconocimiento de entidades legales, análisis de estructura documental, extracción de cláusulas clave."
                    dataSource="Corpus de documentos legales, plantillas jurídicas, bases de jurisprudencia, documentos procesados históricos."
                    updateFrequency="Procesamiento continuo con métricas actualizadas en tiempo real"
                    iconColor="text-gray-400"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Brain className="w-5 h-5 text-purple-600" />
                  <span className="text-sm text-gray-600">Motor Legal</span>
                </div>
              </div>
              
              <div className="space-y-6">
                {documentAnalysis.map((doc, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-gray-900">{doc.type}</h4>
                        <InfoTooltip 
                          title={doc.tooltip.title}
                          description={doc.tooltip.description}
                          aiMethod={doc.tooltip.aiMethod}
                          dataSource={doc.tooltip.dataSource}
                          updateFrequency={doc.tooltip.updateFrequency}
                          iconColor="text-gray-400"
                        />
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getColorClasses(doc.color)}`}>
                        {doc.processed} procesados
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Precisión</p>
                        <p className="text-lg font-bold text-gray-900">{doc.accuracy}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Tiempo Promedio</p>
                        <p className="text-lg font-bold text-gray-900">{doc.avgTime}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Este Mes</p>
                        <p className="text-lg font-bold text-gray-900">{doc.processed}</p>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm text-gray-700">
                        <strong>Insight IA:</strong> {doc.keyInsights}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Progreso de Casos */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Casos Activos con Análisis IA
                </h3>
                <InfoTooltip 
                  title="Gestión Inteligente de Casos Legales"
                  description="Sistema de seguimiento de casos que utiliza IA para predecir outcomes, gestionar plazos y optimizar estrategias legales."
                  aiMethod="Análisis predictivo de resoluciones, gestión automática de plazos, scoring de viabilidad legal, alertas inteligentes."
                  dataSource="Histórico de casos similares, datos de tribunales, expedientes digitales, calendarios procesales."
                  updateFrequency="Actualización diaria con monitoreo continuo de plazos"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-4">
                {caseProgress.map((case_, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">{case_.name}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(case_.priority)}`}>
                        {case_.priority === 'high' ? 'Alta' : case_.priority === 'medium' ? 'Media' : 'Baja'}
                      </span>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Etapa: {case_.stage}</span>
                        <span className="text-sm text-gray-600">Fecha límite: {case_.deadline}</span>
                      </div>
                      
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-600">Progreso</span>
                          <span className="text-sm font-medium">{case_.progress}%</span>
                        </div>
                        <div className="bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              case_.progress >= 75 ? 'bg-green-500' :
                              case_.progress >= 50 ? 'bg-blue-500' : 'bg-yellow-500'
                            }`}
                            style={{ width: `${case_.progress}%` }}
                          />
                        </div>
                      </div>
                      
                      <div className="bg-blue-50 rounded-lg p-3 flex items-start space-x-2">
                        <Brain className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-blue-800">{case_.aiInsights}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Alertas y Notificaciones */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Alertas Inteligentes</h3>
                <InfoTooltip 
                  title="Sistema de Alertas Legales Inteligentes"
                  description="Notificaciones automáticas basadas en IA para identificar oportunidades, riesgos y acciones críticas en la práctica legal."
                  aiMethod="Análisis predictivo de riesgos, monitoreo de plazos legales, detección de patrones de oportunidades."
                  dataSource="Calendarios procesales, bases normativas, histórico de casos, fechas críticas de clientes."
                  updateFrequency="Monitoreo continuo con alertas instantáneas"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-4">
                {alerts.map((alert, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-3">
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-full flex-shrink-0 ${
                        alert.type === 'warning' ? 'bg-yellow-100' :
                        alert.type === 'success' ? 'bg-green-100' : 'bg-blue-100'
                      }`}>
                        {alert.type === 'warning' && <AlertTriangle className="w-4 h-4 text-yellow-600" />}
                        {alert.type === 'success' && <CheckCircle className="w-4 h-4 text-green-600" />}
                        {alert.type === 'info' && <AlertCircle className="w-4 h-4 text-blue-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">{alert.message}</p>
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-xs text-gray-500">{alert.time}</p>
                          <button className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                            {alert.action}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Estadísticas de Investigación */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Investigación Legal IA</h3>
                <InfoTooltip 
                  title="Motor de Investigación Legal Inteligente"
                  description="Sistema de búsqueda semántica especializado en investigación jurídica que encuentra precedentes y análisis relevantes de manera automática."
                  aiMethod="Búsqueda semántica en corpus jurídico, análisis de relevancia por área legal, ranking inteligente de resultados."
                  dataSource="Bases de jurisprudencia, bibliotecas legales digitales, códigos y reglamentos, doctrina jurídica."
                  updateFrequency="Búsquedas en tiempo real con bases actualizadas semanalmente"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-4">
                {researchStats.map((stat, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">{stat.topic}</span>
                      <div className="text-right">
                        <div className="text-sm font-bold text-gray-900">{stat.successRate}%</div>
                        <div className="text-xs text-gray-500">{stat.searches} búsquedas</div>
                      </div>
                    </div>
                    <div className="bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          stat.successRate >= 90 ? 'bg-green-500' :
                          stat.successRate >= 85 ? 'bg-blue-500' : 'bg-yellow-500'
                        }`}
                        style={{ width: `${stat.successRate}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-purple-50 rounded-lg">
                <div className="flex items-center">
                  <Search className="w-5 h-5 text-purple-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-purple-800">Eficiencia Global</p>
                    <p className="text-lg font-bold text-purple-900">89.6%</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Próximas Tareas IA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Tareas Programadas IA</h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium">Revisar 12 contratos</span>
                  </div>
                  <span className="text-xs text-purple-600">Hoy 16:00</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <BookOpen className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium">Investigación jurisprudencia</span>
                  </div>
                  <span className="text-xs text-blue-600">Mañana 9:00</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <BarChart3 className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium">Reporte semanal</span>
                  </div>
                  <span className="text-xs text-green-600">Viernes</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  )
} 